import CartIcon from '@/asset/icons/cart';
import BoldText from '@/components/BoldText';
import CustomHorizonRadio from '@/components/CustomHorizonRadio';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { ONBOARDING_STEP } from '@/constants/enum';
import { THUMBNAIL } from '@/helpers/link';
import { handleSaveOnboardingSettings, onboardingSettingSelector } from '@/redux/slice/onboarding.slice';
import { IResponseApi } from '@/types/apis/response';
import { BlockStack, Box, Button, Card, Icon, InlineGrid, Modal } from '@shopify/polaris';
import { CheckCircleIcon, XCircleIcon, XIcon } from '@shopify/polaris-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonContainerStyled, ImgStyled, MinHeightStyled, RadioTabStyled } from '../styled';

interface IProps {
  shopThemesData: IResponseApi.IGetShopThemes[] | undefined;
  isLoadingShopThemes: boolean;
  handleSkip?: () => void;
}

const OnboardingTheme = ({ shopThemesData, isLoadingShopThemes, handleSkip }: IProps) => {
  const { t } = useTranslation('onboarding');
  const onboardingSettings = useSelector(onboardingSettingSelector);
  const dispatch = useDispatch();
  const appEmbedId = process.env.REACT_APP_SHOPIFY_THEME_APP_EXTENSION_ID;
  const [isFirstEmbedCheck, setIsFirstEmbedCheck] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const isActiveStatus = useMemo(() => {
    const selectedTheme = shopThemesData?.find((item) => item.id === onboardingSettings.theme);
    const settingsFile = selectedTheme?.files.find((f) => f.filename === 'config/settings_data.json');
    const blocks = settingsFile?.file?.current?.blocks;

    if (blocks && selectedTheme && appEmbedId) {
      return Object.values(blocks).some((block: any) => block.type.includes(appEmbedId) && !block.disabled);
    }

    return false;
  }, [appEmbedId, onboardingSettings.theme, shopThemesData]);

  const handleClickEmbedApp = () => {
    setIsFirstEmbedCheck(false);
    const themeId = onboardingSettings.theme.split('/')[onboardingSettings.theme.split('/').length - 1];
    window.open(
      `https://${config.shop}/admin/themes/${themeId}/editor?context=apps&template=cart&activateAppId=${appEmbedId}/app-embed-block.`,
      '_blank',
    );
  };

  const handleFinish = () => {
    if (isActiveStatus) {
      dispatch(handleSaveOnboardingSettings({ step: ONBOARDING_STEP.FINISH }));
    } else {
      setIsOpenModal(true);
    }
  };

  return (
    <Card padding={'400'}>
      <div className="flex justify-end">
        <Button icon={XIcon} variant="tertiary" onClick={handleSkip}></Button>
      </div>
      <MinHeightStyled>
        <div className="flex flex-col justify-between h-full">
          <div className="flex justify-between items-center h-full">
            <InlineGrid columns={{ xs: 1, lg: 2 }} gap={'400'}>
              <div className="flex flex-col gap-16 items-center justify-center">
                <BoldText>{t('embedded.imgTitle')}</BoldText>
                <ImgStyled>
                  <img src={THUMBNAIL.ONBOARDING_THEME_IMG} alt="onboarding_theme_img" />
                </ImgStyled>
              </div>
              <BlockStack>
                <BlockStack gap={'300'}>
                  <div className="flex items-center gap-8">
                    <CartIcon width={24} height={22} />
                    <BoldText variant="headingXl">{t('embedded.title')}</BoldText>
                  </div>
                  <RegularText tone="subdued">{t('embedded.desc')}</RegularText>
                </BlockStack>
                <Box paddingBlockStart={'600'}>
                  <BlockStack gap={'400'}>
                    <div className="flex flex-col gap-4">
                      <ol>
                        <li>{t('embedded.listStep.1')}</li>
                        <li dangerouslySetInnerHTML={{ __html: t('embedded.listStep.2') }} />
                        <li dangerouslySetInnerHTML={{ __html: t('embedded.listStep.3') }} />
                      </ol>
                    </div>
                    {/* {shopThemesData?.find((item) => item.id === onboardingSettings.theme)?.name}:{' '}
                {isActiveStatus ? 'Enabled' : 'Disabled'} */}
                    <div>
                      <Button
                        icon={
                          isActiveStatus ? (
                            <Icon source={CheckCircleIcon} tone="success" />
                          ) : isFirstEmbedCheck ? undefined : (
                            <Icon source={XCircleIcon} tone="critical" />
                          )
                        }
                        tone={isActiveStatus ? 'success' : isFirstEmbedCheck ? undefined : 'critical'}
                        variant={isFirstEmbedCheck && !isActiveStatus ? 'primary' : 'secondary'}
                        onClick={isActiveStatus ? undefined : handleClickEmbedApp}
                        loading={isLoadingShopThemes}
                      >
                        {isActiveStatus
                          ? t('embedded.button.success')
                          : isFirstEmbedCheck
                          ? t('embedded.button.embed')
                          : t('embedded.button.failed')}
                      </Button>
                    </div>
                  </BlockStack>
                </Box>
              </BlockStack>
            </InlineGrid>
          </div>
          <Box>
            <ButtonContainerStyled>
              <RadioTabStyled>
                <CustomHorizonRadio
                  checked={onboardingSettings.step}
                  onChange={(value) => dispatch(handleSaveOnboardingSettings({ step: +value }))}
                  options={[ONBOARDING_STEP.WELCOME, ONBOARDING_STEP.EMBEDDED_APP]}
                />
              </RadioTabStyled>
              <Button variant="primary" onClick={handleFinish}>
                {t('embedded.finish')}
              </Button>
            </ButtonContainerStyled>
          </Box>
        </div>
      </MinHeightStyled>
      <Modal
        title={t('embedded.modal.title')}
        open={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        primaryAction={{
          content: t('embedded.modal.primaryButton'),
          onAction: () => {
            setIsOpenModal(false);
            handleClickEmbedApp();
          },
        }}
        secondaryActions={[
          {
            content: t('embedded.modal.secondaryButton'),
            onAction: () => {
              setIsOpenModal(false);
              handleSkip?.();
            },
          },
        ]}
      >
        <Modal.Section>
          <div>{t('embedded.modal.content-1')}</div>
          <br />
          <div dangerouslySetInnerHTML={{ __html: t('embedded.modal.content-2') }} />
        </Modal.Section>
      </Modal>
    </Card>
  );
};

export default OnboardingTheme;
